import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Car,
  BarChart3,
  Settings,
  MapPin,
  Users,
  Building2,
  Mail,
  FileText,
  X,
  Megaphone,
  UserCircle2,
  Shield,
  Briefcase,
  ChevronRight,
  Bell,
  Key,
  Smartphone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Sidebar = ({ onClose }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Admin menüsü (Sistem Yöneticisi)
  const adminNavigation = [
    {
      name: 'Sistem Yönetimi',
      href: '/admin',
      icon: LayoutDashboard
    },
    {
      name: 'Lokasyon Yönetimi',
      href: '/admin/locations',
      icon: Building2
    },
    {
      name: 'Kullanıcı Yönetimi',
      href: '/admin/users',
      icon: Users
    },
    {
      name: 'Reklam Yönetimi',
      href: '/admin/advertisements',
      icon: Megaphone
    },
    {
      name: 'Bildirimler',
      href: '/admin/notifications',
      icon: Bell
    },
    {
      name: 'Kayıtlı Cihazlar',
      href: '/admin/devices',
      icon: Smartphone
    },
    {
      name: 'İletişim Mesajları',
      href: '/admin/contact-messages',
      icon: Mail
    },
    {
      name: 'Demo Başvuruları',
      href: '/admin/applications',
      icon: FileText
    },
    {
      name: 'Sistem Raporları',
      href: '/admin/reports',
      icon: BarChart3
    },
    {
      name: 'API Anahtarları',
      href: '/admin/api-tokens',
      icon: Key
    },
    {
      name: 'Sistem Ayarları',
      href: '/admin/settings',
      icon: Settings
    }
  ];

  // İşletme sahibi menüsü
  const ownerNavigation = [
    {
      name: 'Ana Sayfa',
      href: '/owner',
      icon: LayoutDashboard
    },
    {
      name: 'Lokasyonlarım',
      href: '/owner/locations',
      icon: MapPin
    },
    {
      name: 'Kullanıcı Yönetimi',
      href: '/owner/users',
      icon: Users
    },
    {
      name: 'Araçlar',
      href: '/owner/vehicles',
      icon: Car
    },
    {
      name: 'Raporlar',
      href: '/owner/reports',
      icon: BarChart3
    },
    {
      name: 'Ayarlar',
      href: '/owner/settings',
      icon: Settings
    }
  ];

  // Personel menüsü
  const staffNavigation = [
    {
      name: 'Ana Sayfa',
      href: '/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Araçlar',
      href: '/vehicles',
      icon: Car
    },
    {
      name: 'Raporlar',
      href: '/staff/reports',
      icon: BarChart3
    },
    {
      name: 'Ayarlar',
      href: '/settings',
      icon: Settings
    }
  ];

  // Kullanıcı rolüne göre menü belirleme
  const getNavigation = () => {
    if (user?.username === 'admin') {
      return adminNavigation;
    } else if (user?.isOwner) {
      return ownerNavigation;
    } else {
      return staffNavigation;
    }
  };

  const navigation = getNavigation();

  // Get user role info
  const getUserRole = () => {
    if (user?.username === 'admin') {
      return { label: 'Sistem Yöneticisi', icon: Shield, color: 'text-yellow-700 bg-yellow-50' };
    } else if (user?.isOwner) {
      return { label: 'İşletme Sahibi', icon: Briefcase, color: 'text-purple-700 bg-purple-50' };
    } else {
      return { label: 'Personel', icon: UserCircle2, color: 'text-blue-700 bg-blue-50' };
    }
  };

  const roleInfo = getUserRole();

  return (
    <div className="flex h-full min-h-screen flex-col bg-gradient-to-b from-gray-50 to-white shadow-xl">
      {/* Header with Logo */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <img src="/logo.png" alt="Veribil Logo" className="h-12 w-auto" />
        </div>

        {/* Close button for mobile */}
        {onClose && (
          <button
            type="button"
            className="lg:hidden rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* User Profile Section */}
      <div className="px-4 py-5 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#7a2c2c] to-[#5a1f1f] flex items-center justify-center shadow-md">
              <UserCircle2 className="h-7 w-7 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.full_name || user?.username || 'Kullanıcı'}
            </p>
            <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${roleInfo.color}`}>
              <roleInfo.icon className="h-3 w-3 mr-1" />
              {roleInfo.label}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {/* Navigation Header */}
        <div className="px-3 mb-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Menü
          </h3>
        </div>

        {navigation.map((item) => {
          const isActive = location.pathname === item.href ||
                          (item.href !== '/dashboard' && item.href !== '/admin' && item.href !== '/owner' && location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`
                relative z-10 group flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                ${isActive
                  ? 'bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f] text-white shadow-lg shadow-[#7a2c2c]/30'
                  : 'text-gray-700 hover:bg-white hover:shadow-md hover:text-[#7a2c2c]'
                }
              `}
              onClick={onClose}
            >
              <div className={`
                flex items-center justify-center w-9 h-9 rounded-lg mr-3 transition-colors
                ${isActive
                  ? 'bg-white/20'
                  : 'bg-gray-100 group-hover:bg-[#f9e6e6]'
                }
              `}>
                <item.icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-600 group-hover:text-[#7a2c2c]'}`} />
              </div>
              <span className="flex-1">{item.name}</span>
              {isActive && (
                <ChevronRight className="h-4 w-4 text-white/70" />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User locations - Only show for non-admin users */}
      {user?.username !== 'admin' && user?.locations && user.locations.length > 0 && (
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Lokasyonlarınız
            </h3>
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#7a2c2c] text-white">
              {user.locations.length}
            </span>
          </div>
          <div className="space-y-2">
            {user.locations.map((location) => (
              <div
                key={location.id}
                className="group flex items-start p-3 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 hover:from-[#f9e6e6] hover:to-red-50 border border-gray-200 hover:border-[#7a2c2c]/30 transition-all duration-200"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm mr-3 flex-shrink-0">
                  <MapPin className="h-4 w-4 text-[#7a2c2c]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{location.name}</p>
                  <p className="text-xs text-gray-600 capitalize mt-0.5">
                    {location.role === 'staff' ? 'Personel' : location.role === 'owner' ? 'Sahip' : location.role}
                    {' • '}
                    {location.type === 'both' ? 'Vale + Yıkama' : location.type === 'valet' ? 'Vale' : location.type === 'wash' ? 'Yıkama' : location.type}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white p-5">
        <div className="flex items-center justify-center space-x-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#7a2c2c] to-[#5a1f1f] flex items-center justify-center shadow-md">
            <span className="text-white font-bold text-base">V</span>
          </div>
          <div className="text-left">
            <p className="text-sm font-bold text-gray-900">Veribil</p>
            <p className="text-[10px] text-gray-500">Araç Yönetim Sistemi</p>
            <p className="text-[9px] text-[#7a2c2c] font-semibold mt-0.5">
              Versiyon {process.env.REACT_APP_VERSION || 'v1.0.0'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;